// Product search for customer estimates: matches brand, product name, pack quantity,
// barcode and (when no quantity matches) MRP, then suggests the nearest packs so a
// missing pack size can be priced from an existing one.

const UNIT_ALIASES={
  kg:['g',1000],kgs:['g',1000],kilo:['g',1000],g:['g',1],gm:['g',1],gms:['g',1],gram:['g',1],grams:['g',1],gr:['g',1],
  l:['ml',1000],lt:['ml',1000],ltr:['ml',1000],ltrs:['ml',1000],litre:['ml',1000],liter:['ml',1000],litres:['ml',1000],ml:['ml',1],
  rs:['rs',1],re:['rs',1],rupee:['rs',1],rupees:['rs',1],
  pc:['pc',1],pcs:['pc',1],piece:['pc',1],pieces:['pc',1],n:['pc',1],no:['pc',1],nos:['pc',1],u:['pc',1],unit:['pc',1],units:['pc',1],
};
export const normalizeUnit=unit=>UNIT_ALIASES[String(unit||'').toLowerCase().replace(/\./g,'')]||null;

// Base quantity (grams, ml, rupee-pack or pieces) so 1 KG and 1000 G compare equal.
export function baseQuantity(quantity,unit){
  const n=Number(quantity),u=normalizeUnit(unit);
  if(!Number.isFinite(n)||n<=0||!u)return null;
  return {dim:u[0],value:n*u[1]};
}

export function packMeta(pack){
  const sizeMatch=String(pack.size||'').trim().match(/^([\d.]+)\s*(.*)$/);
  const quantity=Number.isFinite(Number(pack.quantity))?Number(pack.quantity):sizeMatch?Number(sizeMatch[1]):null;
  const units=pack.units||(sizeMatch?sizeMatch[2]:'');
  return {quantity,units,base:baseQuantity(quantity,units),brand:pack.brand||'',mrp:Number(pack.mrp)>0?Number(pack.mrp):null};
}

export function parseQuery(query){
  const text=[],numbers=[];
  const tokens=String(query||'').toLowerCase().replace(/(\d)([a-z])/g,'$1 $2').split(/[\s,·/]+/).filter(Boolean);
  for(let i=0;i<tokens.length;i++){
    const t=tokens[i];
    if(/^\d+(\.\d+)?$/.test(t)){
      if(t.length>=5){text.push(t);continue;} // long digit runs are barcodes
      const next=tokens[i+1];
      if(next&&normalizeUnit(next)){numbers.push({value:Number(t),unit:next});i++;}
      else numbers.push({value:Number(t),unit:null});
    }else if(t.startsWith('₹')&&Number(t.slice(1))>0)numbers.push({value:Number(t.slice(1)),unit:'₹'});
    else text.push(t.replace(/^₹/,''));
  }
  return {text:text.filter(Boolean),numbers};
}

const sameNumber=(a,b)=>Math.abs(a-b)<1e-9;

function numberMatch(meta,number){
  if(number.unit==='₹')return meta.mrp!=null&&sameNumber(meta.mrp,number.value)?'price':null;
  if(number.unit){
    const wanted=baseQuantity(number.value,number.unit);
    return wanted&&meta.base&&wanted.dim===meta.base.dim&&sameNumber(wanted.value,meta.base.value)?'quantity':null;
  }
  if(meta.quantity!=null&&sameNumber(meta.quantity,number.value))return 'quantity';
  if(meta.mrp!=null&&sameNumber(meta.mrp,number.value))return 'price';
  return null;
}

// Requested base quantity for a pack's dimension; a bare number uses the pack's own unit.
function requestedBase(meta,number){
  if(number.unit==='₹')return null;
  if(number.unit)return baseQuantity(number.value,number.unit);
  return meta.units?baseQuantity(number.value,meta.units):null;
}

/**
 * Returns {matches, calibration}.
 * matches: [{pack, meta, match:'exact'|'quantity'|'price'|'nearest'|'text'}] best first.
 * calibration: when a quantity was typed that no pack has, the nearest existing pack to price it from.
 */
export function searchPacks(packs,query,{warehouse,limit=12}={}){
  const {text,numbers}=parseQuery(query);
  if(!text.length&&!numbers.length)return {matches:[],calibration:null};
  const scored=[];
  for(const pack of packs){
    const meta=packMeta(pack);
    const hay=`${pack.name||''} ${pack.brand||''} ${pack.product||''} ${pack.category||''} ${pack.size||''} ${pack.barcode||''}`.toLowerCase();
    const words=hay.split(/\s+/);
    let score=0,ok=true;
    for(const t of text){
      if(words.some(w=>w.startsWith(t)))score+=3;
      else if(hay.includes(t))score+=1;
      else{ok=false;break;}
    }
    if(!ok)continue;
    const kinds=numbers.map(n=>numberMatch(meta,n));
    if(numbers.length&&kinds.every(Boolean))score+=kinds.includes('quantity')?20:15;
    const inStock=Number(pack.stock?.[warehouse])>0;
    if(inStock)score+=2;
    if(String(pack.brand||'').toLowerCase().startsWith(text[0]||'\u0000'))score+=1;
    scored.push({pack,meta,score,kinds,inStock});
  }
  const exact=numbers.length?scored.filter(s=>s.kinds.every(Boolean)):scored;
  let matches,calibration=null;
  if(!numbers.length||exact.length){
    matches=exact.sort((a,b)=>b.score-a.score||String(a.pack.name).localeCompare(String(b.pack.name))).map(s=>({pack:s.pack,meta:s.meta,match:!numbers.length?'text':s.kinds.includes('quantity')?'quantity':'price'}));
  }else{
    // No pack has the typed size: rank packs of the same product by how close their size is.
    const number=numbers.find(n=>n.unit!=='₹');
    const near=scored.map(s=>{const wanted=number&&requestedBase(s.meta,number);const distance=wanted&&s.meta.base&&wanted.dim===s.meta.base.dim?Math.abs(Math.log(wanted.value/s.meta.base.value)):Infinity;return {...s,wanted,distance};})
      .filter(s=>text.length||Number.isFinite(s.distance));
    near.sort((a,b)=>a.distance-b.distance||b.score-a.score||String(a.pack.name).localeCompare(String(b.pack.name)));
    matches=near.map(s=>({pack:s.pack,meta:s.meta,match:Number.isFinite(s.distance)?'nearest':'text'}));
    const reference=near.find(s=>Number.isFinite(s.distance));
    if(reference&&text.length)calibration={reference:reference.pack,referenceMeta:reference.meta,quantity:number.value,units:number.unit||reference.meta.units,ratio:reference.wanted.value/reference.meta.base.value};
  }
  return {matches:matches.slice(0,limit),calibration};
}

// Price for a requested pack size, scaled from the reference pack's price per unit.
export const calibratedAmount=(referencePrice,ratio)=>Number(referencePrice)>0&&ratio>0?Math.round(Number(referencePrice)*ratio*100)/100:null;
